import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ValuesSection from '../components/ValuesSection';
import QuickAccess from '../components/QuickAccess';
import CtaBanner from '../components/CtaBanner';
import LightboxModal from '../components/LightboxModal';
import { schoolInfo } from '../data/navigationData';

const galleryPhotos = [
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7e157867-df92-46f8-b5ca-20179d4d9044/olf2_62709655.jpg",
    fullUrl: "https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/olf2_62709655.jpg",
    caption: "Trygt og nærværende læringsmiljø",
    span: "col-span-1 md:col-span-2 lg:col-span-2 row-span-2"
  },
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/7e157867-df92-46f8-b5ca-20179d4d9044/2.jpg",
    fullUrl: "https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/2.jpg",
    caption: "Kreative projekter i mindre hold",
    span: "col-span-1 row-span-1"
  },
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/7e157867-df92-46f8-b5ca-20179d4d9044/olf12.jpg",
    fullUrl: "https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/olf12.jpg",
    caption: "Udeliv og frisk luft i skolegården",
    span: "col-span-1 row-span-1"
  },
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/7e157867-df92-46f8-b5ca-20179d4d9044/olf10.jpg",
    fullUrl: "https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/olf10.jpg",
    caption: "Fællesskab på tværs af klasserne",
    span: "col-span-1 row-span-1"
  },
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/7e157867-df92-46f8-b5ca-20179d4d9044/3.jpg",
    fullUrl: "https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/3.jpg",
    caption: "Fordybelse og faglig nysgerrighed",
    span: "col-span-1 row-span-1"
  }
];

export default function HomePage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [latestNews, setLatestNews] = useState([]);
  const [homeContent, setHomeContent] = useState(null);

  // Fetch homepage CMS content
  useEffect(() => {
    fetch('/_api/public/content/forside')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data[0]) {
          setHomeContent(data[0]);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch latest news from CMS
  useEffect(() => {
    fetch('/_api/public/content/nyheder')
      .then((res) => {
        if (!res.ok) throw new Error('CMS news fetch failed');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          // Sort newest first and pick 3
          const sorted = [...data].sort((a, b) => new Date(b.dato || b.date) - new Date(a.dato || a.date));
          const mapped = sorted.map((n) => ({
            id: n.id,
            url_slug: n.url_slug,
            title: n.titel || n.title,
            summary: n.resume || n.summary,
            date: n.dato || n.date,
            image: n.billede || n.image
          }));
          setLatestNews(mapped.slice(0, 3));
        }
      })
      .catch((err) => {
        console.warn('Using fallback news:', err);
      });
  }, []);

  const fullImages = galleryPhotos.map((p) => p.fullUrl);

  return (
    <div className="bg-[#f8faf5]">
      
      {/* 1. FULL WIDTH HERO SECTION */}
      <section className="relative w-full overflow-hidden bg-[#646754] min-h-[480px] sm:min-h-[540px] lg:min-h-[65vh] flex items-end pt-20 sm:pt-28 pb-8 sm:pb-12 lg:pb-14">
        {/* Full-bleed background video */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/odder-video-klippet.jpg"
            className="w-full h-full object-cover object-center scale-105"
          >
            <source src="https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/odder-video-klippet.mp4" type="video/mp4" />
          </video>
          {/* Multi-layer gradient overlays in brand color #646754 */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#646754]/90 via-[#646754]/60 sm:via-[#646754]/35 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#646754]/85 via-[#646754]/30 to-transparent" />
        </div>

        {/* Hero Content positioned at the bottom */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="space-y-5 sm:space-y-7 max-w-4xl">
            
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-white text-xs sm:text-sm font-semibold border border-white/30 shadow-xs">
              <i className="fa-solid fa-graduation-cap text-[#c1d8e5]" aria-hidden="true" />
              <span>{homeContent?.hero_badge || "Prøve- og karakterfri friskole"}</span>
            </div>

            <h1 className="text-2xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white tracking-tight leading-[1.1] font-serif-heading">
              {homeContent?.hero_overskrift || "Glade børn lærer mest"}
            </h1>

            {/* CTAs on the left side - smaller on mobile */}
            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3.5">
              <Link
                to="/skriv-dit-barn-op"
                className="group inline-flex items-center justify-center gap-2 sm:gap-2.5 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-full text-xs sm:text-sm md:text-base font-bold text-[#1D2923] bg-[#c1d8e5] hover:bg-[#a9c9da] hover:shadow-xl hover:-translate-y-0.5 transform transition-all duration-200 active:scale-98 text-center shadow-xs"
              >
                <i className="fa-solid fa-pen-nib text-xs sm:text-sm group-hover:rotate-12 transition-transform duration-200" aria-hidden="true" />
                <span>Skriv dit barn op</span>
              </Link>

              <Link
                to="/besoeg-skolen"
                className="group inline-flex items-center justify-center gap-2 sm:gap-2.5 px-4 sm:px-5 py-2.5 sm:py-3.5 rounded-full text-xs sm:text-sm md:text-base font-semibold text-white bg-transparent border border-white sm:border-2 hover:bg-white hover:text-[#1D2923] hover:shadow-xl hover:-translate-y-0.5 transform transition-all duration-200 active:scale-98 text-center"
              >
                <i className="fa-regular fa-compass text-xs sm:text-sm group-hover:rotate-45 transition-transform duration-200" aria-hidden="true" />
                <span>Besøg skolen</span>
              </Link>
            </div>

            {/* Trust highlights */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-xl pt-1 sm:pt-2">
              <div>
                <div className="text-lg sm:text-2xl lg:text-3xl font-extrabold text-white font-serif-heading">Små</div>
                <div className="text-[11px] sm:text-xs font-medium text-[#C2D4C8] mt-0.5">klassestørrelser</div>
              </div>
              <div>
                <div className="text-lg sm:text-2xl lg:text-3xl font-extrabold text-white font-serif-heading">0. – 9.</div>
                <div className="text-[11px] sm:text-xs font-medium text-[#C2D4C8] mt-0.5">klassetrin</div>
              </div>
              <div>
                <div className="text-lg sm:text-2xl lg:text-3xl font-extrabold text-white font-serif-heading">100%</div>
                <div className="text-[11px] sm:text-xs font-medium text-[#C2D4C8] mt-0.5">prøve- & karakterfri</div>
              </div>
              <div>
                <div className="text-lg sm:text-2xl lg:text-3xl font-extrabold text-white font-serif-heading">Tryghed</div>
                <div className="text-[11px] sm:text-xs font-medium text-[#C2D4C8] mt-0.5">og nærvær i hverdagen</div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 2. HURTIGE INDGANGE */}
      <QuickAccess />

      {/* 3. SKOLENS VÆRDIER */}
      <ValuesSection />

      {/* 3B. FREMHÆVET: PRØVE- OG KARAKTERFRI FRISKOLE */}
      <section className="py-16 lg:py-20 bg-[#264A35] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-[#c1d8e5]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/15 text-[#d4e7d9] text-xs font-bold tracking-wider uppercase border border-white/20">
                <i className="fa-solid fa-graduation-cap" aria-hidden="true" />
                <span>Prøve- og karakterfri skole</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight font-serif-heading leading-tight">
                {homeContent?.karakterfri_overskrift || "Læring med glæde – uden karakterræs og tests"}
              </h2>

              <p className="text-base sm:text-lg text-[#C2D4C8] leading-relaxed">
                {homeContent?.karakterfri_tekst || "På Odder Lille Friskole er vi prøve- og karakterfri. Det betyder, at vi ikke har folkeskolens 9.-klasseprøver, standpunktskarakterer eller nationale tests. Vi tror på, at ægte lærelyst og faglig nysgerrighed trives bedst, når eleverne lærer for livet – og ikke for et tal."}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="bg-white/10 backdrop-blur-xs rounded-2xl p-4 border border-white/10">
                  <div className="text-base font-bold text-white mb-1 font-serif-heading">Ingen tests</div>
                  <p className="text-xs text-[#d4e7d9] leading-relaxed">Fuld undervisningstid helt frem til sommerferien uden afbrydelser af prøver.</p>
                </div>
                <div className="bg-white/10 backdrop-blur-xs rounded-2xl p-4 border border-white/10">
                  <div className="text-base font-bold text-white mb-1 font-serif-heading">Afgangsprojekt</div>
                  <p className="text-xs text-[#d4e7d9] leading-relaxed">Stort tværfagligt 9.-klasse projekt med teori, praksis og fremlæggelse.</p>
                </div>
                <div className="bg-white/10 backdrop-blur-xs rounded-2xl p-4 border border-white/10">
                  <div className="text-base font-bold text-white mb-1 font-serif-heading">Tryg overgang</div>
                  <p className="text-xs text-[#d4e7d9] leading-relaxed">Personlig faglig udtalelse og stærk videregang til ungdomsuddannelserne.</p>
                </div>
              </div>

              <div className="pt-3">
                <Link
                  to="/side/eksamensfri"
                  className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full text-sm font-bold text-[#1D2923] bg-[#c1d8e5] hover:bg-white hover:shadow-lg transition-all duration-200"
                >
                  <span>Læs mere om vores prøve- og karakterfri skole</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/15 bg-white/5 aspect-4/3">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7e157867-df92-46f8-b5ca-20179d4d9044/2.jpg"
                  alt="Elever i fordybelse på Odder Lille Friskole"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end p-6">
                  <blockquote className="text-sm sm:text-base text-white/95 font-medium italic">
                    "Eleverne udvikler sig som hele mennesker med tryghed, selvværd og personlige kompetencer."
                  </blockquote>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. HVERDAGEN I BILLEDER (Editorial Gallery + Lightbox) */}
      <section id="galleri" className="py-16 lg:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1D2923] tracking-tight font-serif-heading">
                Glimt fra livet på skolen
              </h2>
            </div>
            <Link
              to="/gallerier"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#264A35] hover:text-black transition-colors"
            >
              <span>Se alle billedgallerier</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Editorial Photo Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {galleryPhotos.map((photo, index) => (
              <button
                key={photo.url}
                type="button"
                onClick={() => setLightboxIndex(index)}
                className={`group relative rounded-2xl overflow-hidden bg-white shadow-xs hover:shadow-md transition-all duration-300 cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-[#264A35] ${photo.span}`}
                aria-label={`Vis forstørret billede: ${photo.caption}`}
              >
                <img
                  src={photo.url}
                  alt={photo.caption}
                  className="w-full h-full min-h-[200px] object-cover transition-transform duration-500 group-hover:scale-103"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-9 h-9 rounded-full bg-white/90 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[#1D2923]">
                    <i className="fa-solid fa-expand text-xs" aria-hidden="true" />
                  </div>
                </div>
              </button>
            ))}
          </div>

        </div>
      </section>

      {/* 5. NY PÅ SKOLEN SECTION (For prospective parents) */}
      <section id="kommende-foraeldre" className="py-16 lg:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="bg-white rounded-3xl overflow-hidden border border-[#d9e5d4] shadow-md">
            <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
              
              <div className="lg:col-span-7 p-8 sm:p-12 lg:p-14 space-y-6 flex flex-col justify-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1D2923] tracking-tight font-serif-heading">
                  Overvejer du Odder Lille Friskole til dit barn?
                </h2>
                
                <p className="text-base text-[#5A6961] leading-relaxed">
                  Det er et stort valg at vælge skole. Vi gør det let og gennemskueligt for jer at lære os at kende. Her er de typiske spørgsmål fra nye familier:
                </p>

                <div className="space-y-4 pt-2">
                  <div className="flex items-start gap-3">
                    <span className="text-base font-bold text-[#264A35] shrink-0 mt-0.5">1.</span>
                    <div>
                      <h3 className="text-sm font-bold text-[#1D2923]">Hvordan føles hverdagen?</h3>
                      <p className="text-xs sm:text-sm text-[#5A6961]">Mindre klasser, rolig stemning, morgensang hver dag og lærere der har tid til hvert enkelt barn.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="text-base font-bold text-[#264A35] shrink-0 mt-0.5">2.</span>
                    <div>
                      <h3 className="text-sm font-bold text-[#1D2923]">Hvordan kommer vi på besøg?</h3>
                      <p className="text-xs sm:text-sm text-[#5A6961]">I kan altid kontakte os for en uforpligtende rundvisning i undervisningstiden og en snak med skolelederen.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="text-base font-bold text-[#264A35] shrink-0 mt-0.5">3.</span>
                    <div>
                      <h3 className="text-sm font-bold text-[#1D2923]">Hvordan skriver vi vores barn op?</h3>
                      <p className="text-xs sm:text-sm text-[#5A6961]">Vi optager løbende til alle klassetrin med plads. I kan skrive jer op direkte her på siden.</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 flex flex-wrap items-center gap-3">
                  <Link
                    to="/side/derfor-vaelger-familier-os"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-[#264A35] bg-white hover:bg-[#f8faf5] border border-[#d9e5d4] shadow-md hover:shadow-lg transition-all"
                  >
                    <span>Lær skolen at kende</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>

                  <Link
                    to="/besoeg-skolen"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#264A35] hover:underline"
                  >
                    <span>Besøg skolen</span>
                  </Link>

                  <Link
                    to="/skriv-dit-barn-op"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-[#1D2923] bg-[#c1d8e5] hover:bg-[#aecad9] shadow-xs"
                  >
                    <i className="fa-solid fa-pen-nib text-xs" aria-hidden="true" />
                    <span>Skriv dit barn op</span>
                  </Link>
                </div>

              </div>

              {/* Photo on right - full width in its side */}
              <div className="lg:col-span-5 min-h-[300px] sm:min-h-[380px] lg:min-h-full relative">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7e157867-df92-46f8-b5ca-20179d4d9044/olf5.jpg"
                  alt="Børn og pædagog i Fritteren"
                  className="w-full h-full object-cover"
                />
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* 5. NYHEDER (Latest CMS news) */}
      <section id="nyheder" className="py-16 lg:py-24 bg-[#eef4e6] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1D2923] tracking-tight font-serif-heading">
                Seneste nyt fra Odder Lille Friskole
              </h2>
            </div>
            <Link
              to="/nyheder"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#264A35] hover:text-black transition-colors"
            >
              <span>Se alle nyheder</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {latestNews.length > 0 ? (
              latestNews.map((news) => (
                <article
                  key={news.id || news.url_slug}
                  className="bg-white rounded-2xl border border-[#d9e5d4] overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
                >
                  <div>
                    {news.image && news.image[0] && (
                      <div className="h-48 overflow-hidden bg-[#E6DFD5]">
                        <img
                          src={news.image[0]}
                          alt={news.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-[#1D2923] font-serif-heading mb-2">
                        {news.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-[#5A6961] leading-relaxed line-clamp-3">
                        {news.summary}
                      </p>
                    </div>
                  </div>

                  <div className="px-6 pb-6 pt-2">
                    <Link
                      to={`/nyheder/${news.url_slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#264A35] hover:text-black transition-colors"
                    >
                      <span>Læs hele nyheden</span>
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              ))
            ) : (
              <div className="col-span-3 text-center py-8 text-[#5A6961] text-sm">
                Indlæser nyheder...
              </div>
            )}
          </div>

        </div>
      </section>

      {/* 6. PRAKTISK INFORMATION SHORTCUTS */}
      <section id="praktisk" className="py-16 lg:py-20 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl font-bold text-[#1D2923] tracking-tight font-serif-heading">
              Hurtig adgang for nuværende forældre
            </h2>
            <p className="text-sm text-[#5A6961] mt-2">
              Find de vigtigste oplysninger og dokumenter med et enkelt klik.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            
            <Link
              to="/side/praktisk-info"
              className="bg-white hover:border-[#88A68E] hover:shadow-md p-6 rounded-2xl border border-[#d9e5d4] transition-all group flex items-start gap-4 shadow-xs"
            >
              <div className="text-[#264A35] flex items-center justify-center shrink-0 pt-0.5">
                <i className="fa-regular fa-clock text-2xl" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#1D2923] group-hover:text-[#264A35] transition-colors font-serif-heading">
                  Mødetider & Skolepenge
                </h3>
                <p className="text-xs text-[#5A6961] mt-1 leading-relaxed">
                  Morgensang kl. 8.00, SFO åbningstider og takster for skolepenge.
                </p>
              </div>
            </Link>

            <Link
              to="/side/tilsyn-og-evaluering"
              className="bg-white hover:border-[#88A68E] hover:shadow-md p-6 rounded-2xl border border-[#d9e5d4] transition-all group flex items-start gap-4 shadow-xs"
            >
              <div className="text-[#264A35] flex items-center justify-center shrink-0 pt-0.5">
                <i className="fa-regular fa-file-pdf text-2xl" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#1D2923] group-hover:text-[#264A35] transition-colors font-serif-heading">
                  Tilsynsrapporter (PDF)
                </h3>
                <p className="text-xs text-[#5A6961] mt-1 leading-relaxed">
                  Se de seneste officielle tilsynserklæringer og evalueringsrapporter.
                </p>
              </div>
            </Link>

            <Link
              to="/side/vedtaegter-og-politikker"
              className="bg-white hover:border-[#88A68E] hover:shadow-md p-6 rounded-2xl border border-[#d9e5d4] transition-all group flex items-start gap-4 shadow-xs"
            >
              <div className="text-[#264A35] flex items-center justify-center shrink-0 pt-0.5">
                <i className="fa-regular fa-shield-check text-2xl" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#1D2923] group-hover:text-[#264A35] transition-colors font-serif-heading">
                  Vedtægter & Trivselspolitik
                </h3>
                <p className="text-xs text-[#5A6961] mt-1 leading-relaxed">
                  Skolens formelle vedtægter, trivselspolitik og inklusionsproces.
                </p>
              </div>
            </Link>

          </div>

        </div>
      </section>

      {/* 8. CTA BANNER */}
      <CtaBanner />

      {/* Lightbox Modal */}
      <LightboxModal
        images={fullImages}
        activeIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNext={() => setLightboxIndex((prev) => (prev + 1) % fullImages.length)}
        onPrev={() => setLightboxIndex((prev) => (prev - 1 + fullImages.length) % fullImages.length)}
      />

    </div>
  );
}
